/**
=========================================================
* Material Dashboard 2 PRO React - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { connect } from "react-redux";
import PropTypes from "prop-types";
import { useMaterialUIController } from "context";
// @material-ui core components
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";

import { useTranslation } from "react-i18next";
import { Field, reduxForm } from "redux-form";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";

import MDTypography from "components/MDTypography";

// Settings page components
import FormField from "components/FormField";
import FormCheckbox from "components/FormCheckbox";
import { newAlertMessage } from "store/actions/globalAlertActions";

import { sendMetricsLimits } from "store/actions/projectsActions";

import helpers from "../../analytics/AnalyticsHelpers";

const METRICS = helpers?.METRICS;
const CUSTOM_METRICS = helpers?.CUSTOM_METRICS || [];
const STANDARD_METRICS = METRICS.filter((metric) => !CUSTOM_METRICS.includes(metric.key));
const CUSTOM_METRICS_CONFIG = METRICS.filter((metric) => CUSTOM_METRICS.includes(metric.key));

const buildMetricsLimitsPayload = (projectId, data = {}) => {
  const metricsLimits = {};

  METRICS.filter((metric) => helpers?.isIncrementalMetric(metric.key)).forEach(({ key }) => {
    const dailyEnabledField = `${key}DailyLimitEnabled`;
    const monthlyEnabledField = `${key}MonthlyLimitEnabled`;
    const dailyValueField = `${key}DailyLimitValue`;
    const monthlyValueField = `${key}MonthlyLimitValue`;

    const dailyEnabled = Boolean(data[dailyEnabledField]);
    const monthlyEnabled = Boolean(data[monthlyEnabledField]);
    const dailyValue = Number(data[dailyValueField]);
    const monthlyValue = Number(data[monthlyValueField]);

    metricsLimits[key] = {
      dailyEnabled,
      monthlyEnabled,
      dailyValue: Number.isFinite(dailyValue) ? dailyValue : null,
      monthlyValue: Number.isFinite(monthlyValue) ? monthlyValue : null,
    };
  });

  return {
    projectId,
    metricsLimits,
  };
};

const buildMetricsConfigPayload = (data = {}) => {
  const metricsConfig = {};

  Object.entries(data).forEach(([key, value]) => {
    const isLimitField =
      /(DailyLimitEnabled|MonthlyLimitEnabled|DailyLimitValue|MonthlyLimitValue)$/.test(key);

    if (!isLimitField) {
      metricsConfig[key] = value;
    }
  });

  return metricsConfig;
};

function MetricsConfig({
  handleSubmit,
  pristine,
  projectId,
  database,
  actSendMetricsLimits,
  actNewAlertMessage,
}) {
  const { t } = useTranslation();
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;

  const onSubmit = async (data) => {
    const metricsLimitsPayload = buildMetricsLimitsPayload(projectId, data);
    const metricsConfigPayload = buildMetricsConfigPayload(data);

    try {
      await database.editProject({
        uid: projectId,
        metricsConfig: metricsConfigPayload,
      });

      actSendMetricsLimits(metricsLimitsPayload);

      actNewAlertMessage({
        type: "success",
        title: t("metricConfig"),
        content: `${t("configurationSent")}`,
      });
    } catch (error) {
      actNewAlertMessage({
        type: "error",
        title: t("metricConfig"),
        content: `${t("Save")} ${t("failed")}`,
      });
    }
  };

  const renderMetricConfig = (col) => {
    const isCustomMetric = CUSTOM_METRICS.includes(col.key);
    const isWithOutSuffix = col.suffix === null;
    const isIncrementalMetric = helpers?.isIncrementalMetric(col.key);

    return (
      <Grid key={col.key} size={{ xs: 12, sm: 6 }}>
        <MDBox
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1,
            padding: 5,
            border: darkMode ? "3px solid #1a2035" : "1px solid #e5e7eb",
            background: darkMode ? "#1a2035" : "#ffffff",
            borderRadius: 8,
          }}
        >
          <MDTypography
            variant="subtitle1"
            fontWeight="bold"
            color="text"
            sx={{ textTransform: "uppercase", alignSelf: "center", marginBottom: 2 }}
          >
            {t(col.label)}
          </MDTypography>
          <MDBox
            sx={{
              display: "grid",
              gridTemplateColumns: "80px 80px minmax(0, 1fr) 120px",
              columnGap: 2,
              rowGap: 1,
              alignItems: "center",
              width: "100%",
            }}
          >
            <MDTypography variant="button" color="text">
              {t("show")}
            </MDTypography>

            <MDTypography variant="button" color="text">
              {t("showInApp")}
            </MDTypography>

            <MDTypography variant="button" color="text">
              {t("name")}
            </MDTypography>

            {isWithOutSuffix ? (
              <MDTypography variant="button" color="text" sx={{ visibility: "hidden" }}>
                {t("unit")}
              </MDTypography>
            ) : (
              <MDTypography variant="button" color="text">
                {t("unit")}
              </MDTypography>
            )}

            <MDBox sx={{ display: "flex", justifyContent: "center" }}>
              <Field name={`${col.key}Enabled`} component={FormCheckbox} />
            </MDBox>

            <MDBox sx={{ display: "flex", justifyContent: "center" }}>
              <Field name={`${col.key}AppEnabled`} component={FormCheckbox} />
            </MDBox>

            <MDBox sx={{ minWidth: 80, maxWidth: "100%" }}>
              {isCustomMetric ? (
                <Field
                  name={`${col.key}Label`}
                  component={FormField}
                  placeholder={t("customName")}
                />
              ) : (
                <Field
                  name={`${col.key}Label`}
                  component={FormField}
                  disabled
                  format={(value) => value || t(col.label)}
                />
              )}
            </MDBox>

            {isWithOutSuffix ? (
              <MDBox sx={{ width: "100%" }} />
            ) : (
              <MDBox sx={{ width: "100%" }}>
                <Field
                  name={`${col.key}Suffix`}
                  component={FormField}
                  placeholder={col.suffix || ""}
                />
              </MDBox>
            )}
          </MDBox>

          {isIncrementalMetric && (
            <MDBox
              mt={1}
              sx={{
                justifyContent: "center",
                display: "flex",
                flexDirection: "column",
                gap: 1,
                width: "100%",
              }}
            >
              <MDTypography
                sx={{ alignSelf: "center", marginTop: 2 }}
                variant="subtitle2"
                color="text"
                fontWeight="bold"
              >
                {t("limits")}
              </MDTypography>

              <MDBox
                mt={1}
                sx={{
                  display: "grid",
                  gridTemplateColumns: "140px 80px minmax(120px, 180px)",
                  columnGap: 2,
                  rowGap: 1,
                  alignItems: "center",
                }}
              >
                <MDTypography variant="button" color="text">
                  {t("dailyLimit")}
                </MDTypography>

                <MDBox sx={{ display: "flex", justifyContent: "center" }}>
                  <Field name={`${col.key}DailyLimitEnabled`} component={FormCheckbox} />
                </MDBox>

                <MDBox sx={{ width: "100%" }}>
                  <Field
                    name={`${col.key}DailyLimitValue`}
                    component={FormField}
                    type="number"
                    placeholder={t("dailyLimit")}
                  />
                </MDBox>

                <MDTypography variant="button" color="text">
                  {t("monthlyLimit")}
                </MDTypography>

                <MDBox sx={{ display: "flex", justifyContent: "center" }}>
                  <Field name={`${col.key}MonthlyLimitEnabled`} component={FormCheckbox} />
                </MDBox>

                <MDBox sx={{ width: "100%" }}>
                  <Field
                    name={`${col.key}MonthlyLimitValue`}
                    component={FormField}
                    type="number"
                    placeholder={t("monthlyLimit")}
                  />
                </MDBox>
              </MDBox>
            </MDBox>
          )}
        </MDBox>
      </Grid>
    );
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Card id="metrics-config" sx={{ overflow: "visible" }}>
        <MDBox p={3}>
          <MDTypography variant="h4">{`${t("metricConfig").toUpperCase()}`}</MDTypography>
        </MDBox>
        <MDBox pb={3} px={3}>
          <Grid container spacing={3}>
            {STANDARD_METRICS.map((metric) => renderMetricConfig(metric))}

            {CUSTOM_METRICS_CONFIG.length > 0 && (
              <Grid size={{ xs: 12 }} sx={{ marginTop: 5 }}>
                <MDBox mt={1}>
                  <MDTypography
                    variant="h4"
                    fontWeight="bold"
                    color="text"
                    textTransform="uppercase"
                  >
                    {t("customMetrics")}
                  </MDTypography>
                </MDBox>
              </Grid>
            )}

            {CUSTOM_METRICS_CONFIG.map((metric) => renderMetricConfig(metric))}
          </Grid>
          <Grid size={{ xs: 12 }}>
            <MDBox display="flex" justifyContent="center" mt={2} xs={12}>
              <MDButton
                type="submit"
                variant="gradient"
                color="dark"
                size="small"
                disabled={pristine}
              >
                {t("Save")}
              </MDButton>
            </MDBox>
          </Grid>
        </MDBox>
      </Card>
    </form>
  );
}

MetricsConfig.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  pristine: PropTypes.bool.isRequired,
  projectId: PropTypes.string.isRequired,
  database: PropTypes.object.isRequired,
  actSendMetricsLimits: PropTypes.func.isRequired,
  actNewAlertMessage: PropTypes.func.isRequired,
};

const mapStateToProps = (state, props) => {
  const { projectsReducer = {} } = state;
  const projectData = projectsReducer[props?.projectId] || {};
  const metricsConfig = projectData?.metricsConfig || {};
  const metricsLimits = projectData?.onnaDevicesConfig?.metricsLimits || {};
  const metricsDefault = {};

  METRICS.forEach((metric) => {
    const enabledField = `${metric.key}Enabled`;
    const appEnableField = `${metric.key}AppEnabled`;
    const suffixField = `${metric.key}Suffix`;
    const labelField = `${metric.key}Label`;
    const dailyLimitEnabledField = `${metric.key}DailyLimitEnabled`;
    const monthlyLimitEnabledField = `${metric.key}MonthlyLimitEnabled`;
    const dailyLimitValueField = `${metric.key}DailyLimitValue`;
    const monthlyLimitValueField = `${metric.key}MonthlyLimitValue`;

    metricsDefault[enabledField] =
      metricsConfig[enabledField] === undefined ? true : Boolean(metricsConfig[enabledField]);
    metricsDefault[appEnableField] =
      metricsConfig[appEnableField] === undefined ? true : Boolean(metricsConfig[appEnableField]);
    metricsDefault[suffixField] = metricsConfig[suffixField] ?? "";

    if (helpers?.isIncrementalMetric(metric.key)) {
      const metricLimits = metricsLimits[metric.key] || {};

      metricsDefault[dailyLimitEnabledField] =
        metricLimits.dailyEnabled ?? Boolean(metricsConfig[dailyLimitEnabledField]);
      metricsDefault[monthlyLimitEnabledField] =
        metricLimits.monthlyEnabled ?? Boolean(metricsConfig[monthlyLimitEnabledField]);
      metricsDefault[dailyLimitValueField] =
        metricLimits.dailyValue ?? metricsConfig[dailyLimitValueField] ?? "";
      metricsDefault[monthlyLimitValueField] =
        metricLimits.monthlyValue ?? metricsConfig[monthlyLimitValueField] ?? "";
    }

    if (CUSTOM_METRICS.includes(metric.key)) {
      metricsDefault[labelField] = metricsConfig[labelField] ?? "";
    }
  });

  return {
    initialValues: {
      ...metricsConfig,
      ...metricsDefault,
    },
  };
};

const mapDispatchToProps = (dispatch) => ({
  actSendMetricsLimits: (data) => {
    dispatch(sendMetricsLimits(data));
  },
  actNewAlertMessage: (message) => dispatch(newAlertMessage(message)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(
  reduxForm({
    form: "MetricsConfig", // a unique identifier for this form
    enableReinitialize: true,
  })(MetricsConfig)
);
